<script lang="ts" setup>
    import { useDisplay, useTheme } from 'vuetify';
    import { useMenuStore } from '@/stores/menu';
    import { IMenuItem } from '@/types';

    const menuStore = useMenuStore();

    const theme = useTheme();
    const display = useDisplay();
    const route = useRoute();
    const { t } = useI18n();

    const isTouchDevice = computed(() => {
        return 'ontouchstart' in window || display.mobile.value;
    });

    const menuStates = ref<Record<string, boolean>>({});

    const closeMenu = (itemTitle: string) => {
        menuStates.value[itemTitle] = false;
    };

    watch(
        () => route.name,
        () => {
            Object.keys(menuStates.value).forEach((key) => {
                menuStates.value[key] = false;
            });
        }
    );

    onMounted((): void => {
        console.log('Component app-menuNav mounted.');
    });
</script>
<template>
    <v-menu
        offset-y
        :open-on-hover="!isTouchDevice"
        :close-on-content-click="isTouchDevice"
        v-model="menuStates[item.title]"
        v-for="item in menuStore.navMenuItems as IMenuItem[]"
        :key="item.title"
        :open-delay="isTouchDevice ? 0 : 100"
    >
        <template v-slot:activator="{ props }">
            <router-link
                v-if="item.link"
                class="link-plain"
                :to="{
                    name: item.link,
                    params: item.params ? item.params : {},
                }"
            >
                <v-btn
                    tag="a"
                    density="default"
                    color="#cecece"
                    class="font-weight-medium font14 mx-1 px-3"
                    v-bind="props"
                    style="letter-spacing: 0"
                    :class="[item.link === route.name ? 'v-item--active v-list-item--active' : '']"
                >
                    <v-icon v-if="item.icon" :color="item.iconColor" size="20px">
                        {{ item.icon }}
                    </v-icon>
                    <span :class="theme.name.value === 'dark' ? 'text-white' : 'text-black'" class="text-none">
                        {{ t('menu.' + item.title) }}
                    </span>
                </v-btn>
            </router-link>

            <v-btn tag="a" v-else density="default" color="#cecece" class="font-weight-medium font14 mx-1 px-3" v-bind="props" style="letter-spacing: 0">
                <span :class="theme.name.value === 'dark' ? 'text-white' : 'text-black'" class="text-none" v-if="t('menu.' + item.title)">
                    {{ t('menu.' + item.title) }}
                </span>
                <span class="text-none font-weight-bold" v-else>
                    {{ item.title }}
                </span>
                <v-icon size="16" :color="theme.name.value === 'dark' ? '' : 'black'" :icon="mdiChevronDown" />
            </v-btn>
        </template>

        <v-list v-if="item.subItems" class="submenu pa-1" nav>
            <div v-for="subItem in item.subItems" :key="subItem.title" class="pb-1" style="height: 36px">
                <v-list-subheader
                    v-if="!subItem.link"
                    :class="theme.name.value === 'dark' ? 'text-white' : 'text-black'"
                    class="pl-2 font-weight-black text-uppercase pt-1"
                    style="height: 32px"
                >
                    {{ t('menu.' + subItem.title) }}
                </v-list-subheader>
                <router-link
                    v-else
                    :to="{
                        name: subItem.link,
                        params: subItem.params ? subItem.params : {},
                    }"
                    class="text-black link-plain"
                    @click="closeMenu(item.title)"
                >
                    <v-list-item
                        nav
                        base-color="grey"
                        density="compact"
                        class="submenu-item"
                        :class="[subItem.link === route.name ? 'v-item--active v-list-item--active' : '']"
                    >
                        <v-list-item-title :class="theme.name.value === 'dark' ? 'text-white' : 'text-black'" class="font-weight-medium font13">
                            <v-icon v-if="subItem.icon" :color="subItem.iconColor" size="16px" class="mb-1">
                                {{ subItem.icon }}
                            </v-icon>
                            {{ t('menu.' + subItem.title) }}
                        </v-list-item-title>
                    </v-list-item>
                </router-link>
                <v-divider v-if="subItem.divider" class="mt-1"></v-divider>
            </div>
        </v-list>
    </v-menu>
</template>
<style lang="scss" scoped>
    .v-btn {
        transition: all 0.2s ease;

        &:hover:not(.v-item--active) {
            background: rgba(var(--v-theme-tbGreen), 0.08) !important;

            span {
                color: rgb(var(--v-theme-tbGreen)) !important;
            }
        }
    }

    .v-item--active,
    .v-list-item--active {
        background: rgba(var(--v-theme-tbGreen), 0.15) !important;

        span,
        .v-list-item-title {
            color: rgb(var(--v-theme-tbGreen)) !important;
        }
    }

    .submenu {
        padding-bottom: 0 !important;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }

    .submenu-item {
        min-height: 32px;
    }

    .submenu-item :deep(.v-list-item-title) {
        line-height: 1.2;
    }

    .v-list-item {
        transition: all 0.2s ease;

        &:hover:not(.v-list-item--active) {
            background: rgba(var(--v-theme-tbGreen), 0.08) !important;

            .v-list-item-title {
                color: rgb(var(--v-theme-tbGreen)) !important;
            }
        }
    }

    /* Dark theme */
    .v-theme--dark {
        .v-list {
            background: #2b2d31;
        }

        .v-item--active,
        .v-list-item--active {
            background: rgba(var(--v-theme-tbGreen), 0.25) !important;
        }

        .v-list-item {
            &:hover:not(.v-list-item--active) {
                background: rgba(var(--v-theme-tbGreen), 0.15) !important;
            }
        }
    }
</style>
